import { Placeholder } from '@sitecore-jss/sitecore-jss-react';
import React, { useRef } from 'react';
import { useBodyContext } from 'src/components/Common/ContentWrapper/context';
import Portal from 'src/components/Common/Portal';
import SvgLoader from 'src/components/Common/SvgLoader';
import { useExperienceEditor } from 'src/lib/Hooks/useExperienceEditor';
import { useFocusTrap } from 'src/lib/Hooks/useFocusTrap';
import useMediaQuery from 'src/lib/Hooks/useMediaQuery';
import usePreventBodyScroll from 'src/lib/Hooks/usePreventBodyScroll';
import Transition from 'src/lib/Transition';
import { a11yAction } from 'src/lib/Utils/helpers';
import { ModalComponentTypes, ModalTypes } from './types';

const Modal = ({ id, rendering }: ModalTypes) => {
  const { dispatch, state } = useBodyContext();
  const { activeId, isOpen } = state.modal;
  const { isEEActive } = useExperienceEditor();
  const shouldDisplayModal = activeId === id && isOpen;

  return isEEActive ? (
    <div className="border">
      The Modal Container
      <Placeholder name="jss-public-modal-container" rendering={rendering} />
    </div>
  ) : (
    <ModalComponent
      isActive={shouldDisplayModal}
      onCloseHandler={() => dispatch({ type: 'modalClose' })}
      {...{ id }}
    >
      <Placeholder name="jss-public-modal-container" rendering={rendering} />
    </ModalComponent>
  );
};

const ModalComponent = ({
  children,
  controls,
  isActive,
  onCloseHandler = () => {},
}: React.PropsWithChildren<ModalComponentTypes>) => {
  const modalBackgroundRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = !useMediaQuery('md');

  // when the modal is active, do not allow the body to scroll
  usePreventBodyScroll(isActive);

  // classes for the modal 'background' div
  const activeClass = isActive ? 'bg-black bg-opacity-80 pointer-events-auto z-modal' : '';

  // classes for the modal 'container' div
  const containerBackgroundClass = controls?.isTransparent ? '' : 'border border-gray bg-white';
  const containerScrollClass = controls?.preventScroll ? '' : 'overflow-auto';
  const containerClass = `${containerBackgroundClass} ${containerScrollClass}`;

  // closes the modal when clicking outside of the modal
  const handleClose = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event &&
      event.target &&
      event.target instanceof Element &&
      event.target.contains(modalBackgroundRef.current) &&
      isActive
    ) {
      onCloseHandler();
    }
  };

  useFocusTrap({
    shouldTrap: isActive,
    container: modalBackgroundRef.current,
    onExit: () => onCloseHandler(),
    onEnter: ([first]) => first?.focus(),
  });

  return (
    <Portal>
      <div
        className={`${
          // need to add display:none to inactive modals to avoid performance issues which crash page on iphone
          isActive ? '' : 'hidden '
        }js-modal fixed h-screen w-full top-0 left-0 flex flex-col items-center p-12 pointer-events-none ${activeClass}`}
        ref={modalBackgroundRef}
        {...a11yAction(handleClose)}
        role="dialog"
        aria-hidden={!isActive}
        tabIndex={-1}
      >
        <Transition.RevealDown
          active={isActive}
          className={`relative px-24 pt-32 pb-24 m-auto w-full max-w-3xl ${containerClass}`}
        >
          <div ref={modalContainerRef}>
            <button
              aria-label="close"
              className="visible absolute right-0 top-0 mr-24 mt-16 z-overlay"
              onClick={onCloseHandler}
            >
              <SvgLoader
                aria-hidden={true}
                focusable={false}
                color={controls?.isTransparent ? 'text-white' : 'text-teal-dark'}
                name="X"
                size={isMobile ? 20 : 24}
              />
            </button>
            {children}
          </div>
        </Transition.RevealDown>
      </div>
    </Portal>
  );
};

export { Modal as default, ModalComponent, Portal };
